import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import OrderDetailsModal from '../components/OrderDetailsModal';

export default function OrderHistory({ isDarkMode }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [activeTab, setActiveTab] = useState('completed'); // 'completed' | 'cancelled'

    useEffect(() => {
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        try {
            const res = await api.get('/restaurant/orders?status=completed');
            setOrders(res.data.orders);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const styles = getStyles(isDarkMode);

    const getStatusStyle = (status) => {
        let bg = '#dfe6e9';
        let color = '#636e72';

        if (status === 'COMPLETED') { bg = '#2ecc7133'; color = '#2ecc71'; }
        if (status === 'DISPATCHED') { bg = '#9b59b633'; color = '#9b59b6'; }
        if (status === 'REJECTED' || status === 'CANCELLED') { bg = '#e74c3c33'; color = '#e74c3c'; }

        return {
            backgroundColor: bg,
            color: color,
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase'
        };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    // Filter Filters
    const completedOrders = orders?.filter(o => ['COMPLETED', 'DISPATCHED'].includes(o.status)) || [];
    const cancelledOrders = orders?.filter(o => ['CANCELLED', 'REJECTED'].includes(o.status)) || [];

    const displayedOrders = activeTab === 'completed' ? completedOrders : cancelledOrders;

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#fff' : '#333' }}>Loading Order History...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h2 style={styles.title}>Order History</h2>
                    <p style={styles.subtitle}>Past, completed, and cancelled orders.</p>
                </div>
                <div style={styles.actions}>
                    <div style={styles.toggleGroup}>
                        <button
                            style={{ ...styles.toggleBtn, ...(activeTab === 'completed' ? styles.toggleBtnActive : {}) }}
                            onClick={() => setActiveTab('completed')}
                        >
                            Completed ({completedOrders.length})
                        </button>
                        <button
                            style={{ ...styles.toggleBtn, ...(activeTab === 'cancelled' ? styles.toggleBtnActive : {}) }}
                            onClick={() => setActiveTab('cancelled')}
                        >
                            Cancelled/Rejected ({cancelledOrders.length})
                        </button>
                    </div>
                    <button onClick={fetchHistory} style={styles.refreshBtn}>🔄 Refresh</button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr style={styles.headerRow}>
                            <th style={styles.th}>Order ID</th>
                            <th style={styles.th}>Date & Time</th>
                            <th style={styles.th}>Customer</th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Total</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedOrders.length === 0 && (
                            <tr>
                                <td colSpan="7" style={styles.empty}>
                                    No {activeTab} orders found.
                                </td>
                            </tr>
                        )}
                        {displayedOrders.map(order => (
                            <tr key={order.id} style={styles.row}>
                                <td style={{ ...styles.td, fontWeight: '600' }}>{order.orderNumber}</td>
                                <td style={{ ...styles.td, color: isDarkMode ? '#aaa' : '#666' }}>
                                    {new Date(order.createdAt).toLocaleString()}
                                </td>
                                <td style={styles.td}>
                                    <div style={{ fontWeight: '500' }}>{order.customerName || 'Guest'}</div>
                                    <div style={{ fontSize: '0.8rem', color: isDarkMode ? '#888' : '#aaa' }}>{order.customerPhone}</div>
                                </td>
                                <td style={styles.td}>
                                    <span style={styles.typeBadge}>{order.orderType}</span>
                                </td>
                                <td style={{ ...styles.td, fontWeight: '700' }}>{formatCurrency(order.totalAmount)}</td>
                                <td style={styles.td}>
                                    <span style={getStatusStyle(order.status)}>{order.status}</span>
                                </td>
                                <td style={styles.td}>
                                    <button
                                        onClick={() => setSelectedOrder(order)}
                                        style={styles.viewBtn}
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <OrderDetailsModal
                order={selectedOrder}
                isOpen={!!selectedOrder}
                onClose={() => setSelectedOrder(null)}
                // No update status handler needed for history, mostly read-only
                isDarkMode={isDarkMode}
            />
        </div>
    );
}

const getStyles = (isDark) => ({
    container: {
        padding: '24px 32px',
        color: isDark ? '#fff' : '#2D3436'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    title: {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: '700'
    },
    subtitle: {
        margin: '4px 0 0 0',
        color: isDark ? '#888' : '#636E72',
        fontSize: '0.9rem'
    },
    refreshBtn: {
        padding: '8px 16px',
        borderRadius: '8px',
        border: isDark ? '1px solid #444' : '1px solid #ddd',
        backgroundColor: isDark ? '#2D3436' : '#fff',
        color: isDark ? '#ccc' : '#2D3436',
        fontWeight: '600',
        cursor: 'pointer'
    },
    actions: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    toggleGroup: {
        display: 'flex',
        backgroundColor: isDark ? '#333' : '#e0e0e0',
        borderRadius: '8px',
        padding: '4px',
    },
    toggleBtn: {
        padding: '6px 16px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: isDark ? '#aaa' : '#666',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '0.9rem'
    },
    toggleBtnActive: {
        backgroundColor: isDark ? '#555' : '#fff',
        color: isDark ? '#fff' : '#333',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    tableContainer: {
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)',
        border: isDark ? '1px solid #333' : '1px solid #e0e0e0'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    headerRow: {
        backgroundColor: isDark ? '#2d3436' : '#f9f9f9',
        borderBottom: isDark ? '2px solid #444' : '2px solid #eee'
    },
    th: {
        padding: '16px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.9rem',
        color: isDark ? '#ccc' : '#555'
    },
    row: {
        borderBottom: isDark ? '1px solid #333' : '1px solid #f0f0f0',
        transition: 'background-color 0.2s'
    },
    td: {
        padding: '16px',
        verticalAlign: 'middle',
        fontSize: '0.95rem'
    },
    empty: {
        textAlign: 'center',
        padding: '40px',
        color: '#888'
    },
    typeBadge: {
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.8rem',
        backgroundColor: isDark ? '#333' : '#f0f0f0',
        color: isDark ? '#ccc' : '#555'
    },
    viewBtn: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: isDark ? '1px solid #555' : '1px solid #ccc',
        backgroundColor: 'transparent',
        color: isDark ? '#ccc' : '#555',
        cursor: 'pointer',
        fontSize: '0.8rem'
    }
});
